import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  InfoOutlined,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";

const CDCConfiguration = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
    title: "",
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [newRecord, setNewRecord] = useState({
    desc_CDC: "",
    cdc: "",
    coord: "",
    dpt: "",
    responsabile: "",
    local_Corporate: "",
  });
  const [filtersActive, setFiltersActive] = useState(false);

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type, title: "" });
  };

  const handleAddDialogOpen = () => {
    setAddDialog({ open: true });
  };

  const handleAddDialogClose = () => {
    setAddDialog({ open: false });
    setNewRecord({
      desc_CDC: "",
      cdc: "",
      coord: "",
      dpt: "",
      responsabile: "",
      local_Corporate: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getCDCConfigurations();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    } 
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table?.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Configurazione CDC");
      XLSX.writeFile(wb, "Configurazione_CDC_Data.xlsx");
    }
  };

  const handleAddNew = async () => {
    if (
      !newRecord.cdc ||
      !newRecord.desc_CDC ||
      !newRecord.coord ||
      !newRecord.responsabile ||
      !newRecord.local_Corporate
    ) {
      showNotification("Compilare tutti i campi obbligatori", "error");
      return;
    }
    try {
      await LevelOpexService.insertCDCConfiguration(newRecord);
      await fetchData();
      showNotification("Record aggiunto con successo", "success");
      handleAddDialogClose();
    } catch (error) {
      console.error("Error adding new record:", error);
      showNotification("Error adding new record", "error");
    }
  };

  const handleDeleteSelected = () => {
    const selectedRows = table?.getSelectedRows();
    if (selectedRows.length === 0) {
      showNotification("Selezionare i record da eliminare", "default");
      return;
    }
    const selectedIds = selectedRows.map((row) => row.getData()?.id);
    setDeleteDialog({ open: true, ids: selectedIds });
  };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await LevelOpexService.deleteCDCConfiguration(id);
      }
      await fetchData();
      showNotification(
        `${ids.length} record(s) eliminati con successo`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting records:", error);
      showNotification("Error deleting records", "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, ids: [] });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.ids.length > 0) {
      await handleDelete(deleteDialog.ids);
      setDeleteDialog({ open: false, ids: [] });
    }
  };
  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }
      const columns = [
        {
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          titleFormatterParams: { rowRange: "all" },
          hozAlign: "center",
          headerSort: false,
          width: 30,
          frozen: true,
          visible: true,
          cssClass: "frozen-column",
          columnType: "selection",
          cellClick: function (e, cell) {
            const row = cell.getRow();
            
            if (row.isSelected()) {
              row.deselect();
            } else {
              row.select();
            }
          },
        },
        {
          title: "CDC",
          field: "cdc",
          widthGrow: 1,
          editor: "input",
          validator: ["required"],
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Descrizione",
          field: "desc_CDC",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Coord",
          field: "coord",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "DPT",
          field: "dpt",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Responsabile",
          field: "responsabile",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Local/Corporate",
          field: "local_Corporate",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "cdc", dir: "asc" }],
        rowHeight: 40,
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: false,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        selectableRangeColumns: false,
        selectableRangeRows: true,
        selectableRangeClearCells: true,
        clipboard: true,
        clipboardCopyRowRange: "range",
        clipboardPasteParser: "range",
        clipboardPasteAction: "range",
        editTriggerEvent: "dblclick",
        navigationMode: "cell",
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
      });

      newTable.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row?.getData();

          const updatedData = await LevelOpexService.updateCDCConfiguration(
            rowData
          );
          cell.getTable().updateData([updatedData]);
          showNotification("Dati salvati con successo", "success");
        } catch (error) {
          console.error("Error saving data:", error);
          cell.restoreOldValue();
          showNotification("Error saving changes", "error");
        }
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
  }, [table]);
  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Configurazione CDC", href: "/cdc-configuration" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Configurazione CDC
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="primary"
            label="Aggiungi nuovo"
            icon={<AddIcon />}
            onClick={handleAddDialogOpen}
            size="small"
          />
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi filtri" : "Mostra filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);

                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
          <Q8Button
            variant="primary"
            label="Elimina selezionati"
            icon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          mb: 2,
          bgcolor: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#FB8C00" }} />
        <Typography variant="body2">
          Per modificare un record, seleziona una colonna modificabile (colonne
          gialle) e fai doppio clic sulla cella per apportare una modifica.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Dialog
        open={addDialog.open}
        onClose={handleAddDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Aggiungi nuovo CDC</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              py: 2,
            }}
          >
            <TextField
              label="CDC"
              name="cdc"
              className="inputFieldCustom"
              value={newRecord.cdc}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.cdc}
              helperText={!newRecord.cdc ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="Descrizione"
              name="desc_CDC"
              className="inputFieldCustom"
              value={newRecord.desc_CDC}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.desc_CDC}
              helperText={!newRecord.desc_CDC ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="Coord"
              name="coord"
              value={newRecord.coord}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.coord}
              helperText={!newRecord.coord ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="DPT"
              name="dpt"
              value={newRecord.dpt}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Responsabile"
              name="responsabile"
              value={newRecord.responsabile}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.responsabile}
              helperText={!newRecord.responsabile ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="Local/Corporate"
              name="local_Corporate"
              value={newRecord.local_Corporate}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.local_Corporate}
              helperText={
                !newRecord.local_Corporate ? "Campo obbligatorio" : ""
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleAddDialogClose}
          />
          <Q8Button variant="primary" label="Aggiungi" onClick={handleAddNew} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Confermi di voler eliminare {deleteDialog.ids.length} record?
            Quest'azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleDeleteCancel}
          />
          <Q8Button
            variant="primary"
            label="Elimina"
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: "#cc0e00", color: "white" }}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        title={snackbar.title}
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default CDCConfiguration;
